import React, { useState } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { Role } from '../../../domain/usecases/Roles'
import { UserWithRole } from '../../../domain/usecases/UserRoles'
import { DataScopeSelector } from './DataScopeSelector'
import { RolePermissionsDisplay } from './RolePermissionsDisplay'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'
import { throwIfHttpError } from '../httpError'

interface EditUserRoleModalProps {
  show: boolean
  onHide: () => void
  onSuccess: () => void
  user: UserWithRole
  roles: Role[]
  companyId: string
}

interface FormData {
  role_id: string
  scope_type: 'full' | 'restricted'
  scope_resources: string[]
}

export const EditUserRoleModal: React.FC<EditUserRoleModalProps> = ({
  show,
  onHide,
  onSuccess,
  user,
  roles,
  companyId
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      role_id: user.role_id,
      scope_type: user.scope_type,
      scope_resources: user.scope_resources || []
    }
  })
  const httpClient = useAxiosHttpClient()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const scopeType = watch('scope_type')
  const selectedRoleId = watch('role_id')

  const selectedRole = roles.find((r) => r.id === selectedRoleId)
  const isSystemRole = selectedRole ? 'is_system' in selectedRole : false

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    setError(null)

    try {
      if (!user.user_id) {
        throw new Error(
          'Não é possível editar convites pendentes. O usuário deve aceitar o convite primeiro.'
        )
      }

      // Use the new update endpoint instead of assign
      const response = await httpClient.post?.({
        url: `${URL_API}/user-roles/${user.user_id}/role`,
        data: {
          company_id: companyId,
          role_id: data.role_id,
          is_system_role: isSystemRole,
          scope_type: data.scope_type,
          scope_entity: data.scope_type === 'restricted' ? 'spe' : null,
          scope_resources:
            data.scope_type === 'restricted' ? data.scope_resources : null
        }
      })
      throwIfHttpError(response, 'Erro ao atualizar função do usuário')

      onSuccess()
    } catch (err: any) {
      console.error('Error updating user role:', err)
      setError(err.message || 'Erro ao atualizar função do usuário')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Função do Usuário</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="mb-3">
            <strong>Usuário:</strong> {user.user_name} ({user.user_email})
          </div>

          <Form.Group>
            <Form.Label>Função *</Form.Label>
            <Form.Control
              as="select"
              {...register('role_id', { required: 'Função é obrigatória' })}
              isInvalid={!!errors.role_id}
            >
              <option value="">Selecione uma função...</option>
              <optgroup label="Funções do Sistema">
                {roles
                  .filter((r) => 'is_system' in r && r.is_system)
                  .map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.display_name}
                    </option>
                  ))}
              </optgroup>
              {roles.filter((r) => !('is_system' in r) || !r.is_system).length >
                0 && (
                <optgroup label="Funções Personalizadas">
                  {roles
                    .filter((r) => !('is_system' in r) || !r.is_system)
                    .map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.display_name}
                      </option>
                    ))}
                </optgroup>
              )}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.role_id?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Escopo de Acesso *</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="edit-scope-full"
                label="Acesso Completo (Todas as SPEs)"
                value="full"
                {...register('scope_type')}
              />
              <Form.Check
                type="radio"
                id="edit-scope-restricted"
                label="Acesso Restrito (Selecionar SPEs específicas)"
                value="restricted"
                {...register('scope_type')}
              />
            </div>
          </Form.Group>

          {selectedRoleId && (
            <RolePermissionsDisplay
              roleId={selectedRoleId}
              isSystemRole={isSystemRole}
            />
          )}

          {scopeType === 'restricted' && (
            <DataScopeSelector
              companyId={companyId}
              selectedResources={watch('scope_resources') || []}
              onChange={(resources) => setValue('scope_resources', resources)}
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Salvando...
              </>
            ) : (
              'Salvar Alterações'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
